import { useEffect, useMemo, useState } from "react"
import { LoaderFunctionArgs, useLoaderData, useRevalidator, useSearchParams } from "react-router-dom"
import { eachMonthOfInterval, format, subMonths } from "date-fns"
import * as DM from "@radix-ui/react-dropdown-menu"
import {
  faAngleDown,
  faArrowsRotate,
  faClock,
  faEllipsisVertical,
  faListUl,
  faMinus,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { cx, fetchAPI, config } from "../utils"
import { Header } from "../components"

interface NodeInfo {
  averageTimeTakenMs: null | number
  idle: number
  processing: number
  threads: number
  type: string
}

interface UtilisationStatisticsInfo {
  longestIdleThread: null | number
  longestProcessingTime: null | number
  shortestProcessingTime: null | number
}

interface StatisticsInfo {
  statistics: NodeInfo[]
  utilisationStatistics: UtilisationStatisticsInfo
}

interface OverviewPanelData {
  serverName: string
  averageTimeTaken: number
  count: number
}

interface LoaderResponse {
  panelData: StatisticsInfo | undefined
  serverListData: string[]
  queryParams: {
    dateRange: string
    serverName: string
    searchExpandedType: string
  }
  overviewPanel: Array<{
    type: string
    overviewPanelRes: Record<string, OverviewPanelData[]>
  }>
}

async function getProcessOverview(type: string) {
  const overviewPanelRes = await fetchAPI<Record<string, OverviewPanelData[]>>(
    `/api/DocumentServer/ProcessOverview/${type}`,
  )
  return { type, overviewPanelRes: overviewPanelRes.result ?? {} }
}

async function loader({ request }: LoaderFunctionArgs): Promise<LoaderResponse> {
  if (!config.documentServerEnabled) {
    throw new Response("Locked", { status: 423 })
  }

  const url = new URL(request.url)
  const dateRange = url.searchParams.get("dateRange") ?? "today"
  const serverName = url.searchParams.get("serverName") ?? "all"
  const searchExpandedType = url.searchParams.get("processOverview") ?? ""
  const [serverListData, panelData, ...overviewPanel] = await Promise.all([
    fetchAPI<string[]>("/api/DocumentServer/ServerList"),
    fetchAPI<StatisticsInfo>(`/api/DocumentServer/Overview/${dateRange}/${serverName}`),
    ...searchExpandedType.split(",").filter(Boolean).map(getProcessOverview),
  ])

  const queryParams = { dateRange, serverName, searchExpandedType }
  return {
    panelData: panelData.result,
    serverListData: serverListData.result ?? [],
    queryParams,
    overviewPanel,
  }
}

function DashboardPanel({ type }: { type: string }) {
  const { panelData, overviewPanel } = useLoaderData() as LoaderResponse
  const [searchParams, setSearchParams] = useSearchParams()
  const node = panelData?.statistics?.find((a) => a.type === type)

  if (node == null) return null

  const averageTime = node.averageTimeTakenMs === null ? 0 : node.averageTimeTakenMs.toFixed(2)
  const title = node.type.replace(/([A-Z])/g, " $1").trim()
  const loadColor = getLoadLevelColor(node)

  const expandedList = searchParams.get("processOverview")?.split(",").filter(Boolean) ?? []

  const isExpandedPan = expandedList.includes(type)
  const detailedPanel = overviewPanel.find((p) => p.type === type)

  const panelsAfterToggle = isExpandedPan ? expandedList.filter((t) => t !== type) : [...expandedList, type]

  const items = [
    { icon: faClock, label: "averageTimeTaken", content: averageTime },
    { icon: faListUl, label: "threadCounts", content: node.threads },
    {
      icon: faArrowsRotate,
      label: "threadProcessing",
      content: node.processing,
    },
    { icon: faMinus, label: "threadIdle", content: node.idle },
  ]

  return (
    <div className={cx(`card mb-8 space-y-5 rounded-lg bg-base-100 pb-4 shadow-xl`, { "row-span-2": isExpandedPan })}>
      <button
        type="button"
        onClick={() => {
          if (panelsAfterToggle.toString().length === 0) searchParams.delete("processOverview")
          else searchParams.set("processOverview", panelsAfterToggle.toString())
          setSearchParams(searchParams)
        }}
        className={cx("rounded-t-lg py-2 text-center", {
          "bg-success text-success-content": loadColor === "success",
          "bg-warning text-warning-content": loadColor === "warning",
          "bg-info text-info-content": loadColor === "info",
        })}
      >
        {title}
      </button>

      {items.map((item) => {
        const msUnit = item.label === "averageTimeTaken" ? "ms" : ""
        const label = item.label.replace(/([A-Z])/g, " $1").trim()
        return (
          <div key={item.label + title}>
            <ul className="flex flex-wrap items-center justify-between px-2">
              <li className="flex flex-wrap items-center text-sm font-medium">
                <FontAwesomeIcon icon={item.icon} className="mx-2 text-base-300" />
                <span className="capitalize">{label}</span>
              </li>
              <li>
                {item.content} {msUnit}
              </li>
            </ul>
            {isExpandedPan &&
              detailedPanel?.overviewPanelRes?.[item.label]?.map((detail) => (
                <li
                  className="flex flex-wrap items-center justify-between pl-10 pr-2 text-sm font-normal"
                  key={detail.serverName}
                >
                  <span>{detail.serverName}</span>
                  <span>
                    {detail.averageTimeTaken ?? detail.count} {msUnit}
                  </span>
                </li>
              ))}
          </div>
        )
      })}
    </div>
  )
}

const Route = () => {
  const { panelData, serverListData } = useLoaderData() as LoaderResponse
  const [refreshInterval, setRefreshInterval] = useState({
    title: "5 seconds",
    value: 5000,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const revalidator = useRevalidator()
  const previousMonths = getAllMonths()
  const dateRange = searchParams.get("dateRange")
  const serverName = searchParams.get("serverName")
  const isFromDropdown = dateRange?.includes("2022")
  const allServerListsData = ["All servers", ...serverListData]

  useEffect(() => {
    if (refreshInterval !== undefined && refreshInterval.value > 0) {
      const interval = setInterval(() => {
        if (revalidator.state === "idle") {
          revalidator.revalidate()
        }
      }, refreshInterval.value)
      return () => clearInterval(interval)
    }
  }, [refreshInterval, revalidator])

  const utilisationStatisticsEntries = useMemo(() => {
    if (panelData?.utilisationStatistics == null) return []
    return Object.entries(panelData.utilisationStatistics).filter(([, item]) => item != null)
  }, [panelData?.utilisationStatistics])

  return (
    <div className="flex h-screen flex-col items-start justify-start overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5">
      <div className="w-full space-y-5">
        <Header />
        <div className="mb-2 flex items-center space-x-2">
          {["Today", "This Week", "Last Week", "This Month"].map((item) => (
            <button
              type="button"
              onClick={() => {
                searchParams.set("dateRange", item)
                setSearchParams(searchParams)
              }}
              key={item}
              className={cx("btn-accent btn-sm btn capitalize", {
                "btn-outline": dateRange !== item || isFromDropdown === null,
              })}
            >
              {item}
            </button>
          ))}
          <DM.Root>
            <DM.Trigger asChild>
              <button
                type="button"
                className={cx("btn-accent btn-sm btn whitespace-pre capitalize", {
                  "btn-outline": isFromDropdown !== null,
                })}
              >
                {isFromDropdown === null ? dateRange : "Previous Months"}{" "}
                <FontAwesomeIcon icon={faEllipsisVertical} className="ml-5" />
              </button>
            </DM.Trigger>
            <DM.Portal>
              <DM.Content>
                <ul className="menu rounded-box mt-1 w-48 bg-base-100">
                  {previousMonths.map((month) => (
                    <DM.Item asChild key={month}>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            searchParams.set("dateRange", month)
                            setSearchParams(searchParams)
                          }}
                        >
                          {month}
                        </button>
                      </li>
                    </DM.Item>
                  ))}
                </ul>
              </DM.Content>
            </DM.Portal>
          </DM.Root>
        </div>
        <div className="mr-0 flex flex-row flex-wrap items-center md:mr-5">
          <span className="mr-5 text-lg text-base-content">You are viewing</span>
          <DM.Root>
            <DM.Trigger asChild>
              <button type="button" className="btn-outline btn-accent btn-sm btn whitespace-pre bg-base-100 capitalize">
                {serverName === null || serverName === "all" ? allServerListsData[0] : serverName}
                <FontAwesomeIcon icon={faAngleDown} className="ml-8" />
              </button>
            </DM.Trigger>
            <DM.Portal>
              <DM.Content>
                <ul className="menu rounded-box mt-1 w-48 bg-base-100">
                  {allServerListsData.map((ser) => {
                    return (
                      <DM.Item asChild key={ser}>
                        <li>
                          <button
                            type="button"
                            onClick={() => {
                              searchParams.set("serverName", ser === allServerListsData[0] ? "all" : ser)
                              setSearchParams(searchParams)
                            }}
                          >
                            {ser}
                          </button>
                        </li>
                      </DM.Item>
                    )
                  })}
                </ul>
              </DM.Content>
            </DM.Portal>
          </DM.Root>
          <span className="mx-5 text-lg text-base-content">Auto refresh every</span>
          <DM.Root>
            <DM.Trigger asChild>
              <button type="button" className="btn-outline btn-accent btn-sm btn whitespace-pre bg-base-100 capitalize">
                {refreshInterval.title} <FontAwesomeIcon icon={faAngleDown} className="ml-8" />
              </button>
            </DM.Trigger>
            <DM.Portal>
              <DM.Content>
                <ul className="menu rounded-box mt-1 w-48 bg-base-100">
                  {autoRefreshedSeconds.map((sec) => (
                    <DM.Item asChild key={sec.title}>
                      <li onClick={() => setRefreshInterval(sec)}>
                        <span>{sec.title}</span>
                      </li>
                    </DM.Item>
                  ))}
                </ul>
              </DM.Content>
            </DM.Portal>
            {revalidator.state !== "idle" && <FontAwesomeIcon icon={faArrowsRotate} className="ml-3 text-accent" />}
          </DM.Root>
        </div>
      </div>
      {utilisationStatisticsEntries.length !== 0 ? (
        <section className="w-full">
          <h4 className="size-20 ml-1 pt-5 pb-2 text-xl font-semibold text-base-content">Stats from Last Seven Days</h4>
          <ul className="flex flex-wrap space-x-3">
            {utilisationStatisticsEntries.map(([title, content]) => {
              const statTitle = title.replace(/([A-Z])/g, " $1").trim()

              return (
                <li key={title} className="card flex-1 justify-center bg-base-100 shadow-xl">
                  <p className="font-regular card-body p-3 text-lg text-grayish">
                    <span className="capitalize">{statTitle}</span>
                    <b className="text-2xl text-base-content">{content} ms</b>
                  </p>
                </li>
              )
            })}
          </ul>
        </section>
      ) : (
        ""
      )}
      <div className="divider"></div>
      <div className="grid w-full grid-cols-2 gap-x-8 font-medium md:grid-cols-3 lg:grid-cols-4">
        {sortingArray.map((type) => (
          <DashboardPanel key={type} type={type} />
        ))}
      </div>
    </div>
  )
}

Route.loader = loader

export default Route

const sortingArray = [
  "BinaryCollection",
  "DocumentAttributesGeneration",
  "GenerateCDM",
  "MissingJpg",
  "JpgGeneration",
  "MissingArtifacts",
  "ArtifactGeneration",
  "SearchRequest",
]

const autoRefreshedSeconds = [
  { title: "1 second", value: 1000 },
  { title: "5 seconds", value: 5000 },
  { title: "30 seconds", value: 30000 },
  { title: "60 seconds", value: 60000 },
]

function getLoadLevelColor(node: NodeInfo) {
  const threadsProcessing = node.processing
  const threadsIdle = node.idle
  const threadsCount = node.threads
  if (threadsIdle === 0) {
    return "warning"
  }
  if (threadsIdle === threadsCount) {
    return "success"
  }
  if (threadsProcessing !== 0) {
    return "info"
  }
}

function getAllMonths() {
  const arrayOfMonths = eachMonthOfInterval({
    start: subMonths(new Date(), 6),
    end: subMonths(new Date(), 1),
  })
  return arrayOfMonths.map((month) => format(month, "LLLL yyyy"))
}
